/* eslint-disable no-console*/

import Immutable from 'immutable';
import { createStructuredSelector } from 'reselect';

import ApiService from 'services/ApiService/ApiService';
import {
  AUTHENTICATED_CHAT_REQUEST_URL,
  GUEST_CHAT_REQUEST_URL,
} from 'webchat/Api';

import { FirebaseMain, initFirebase } from '../firebase';
import firebaseNode from '../firebaseNode';


const SET_AUTH_DATA = 'cam4/firebaseAuth/SET_AUTH_DATA';
const SET_IS_CHAT_AUTH = 'cam4/firebaseAuth/SET_IS_CHAT_AUTH';
const SET_SERVER_OFFSET = 'cam4/firebaseAuth/SET_SERVER_OFFSET';
const UPDATE_AUTH_ERROR = 'cam4/firebaseAuth/UPDATE_AUTH_ERROR';
const UPDATE_GUEST_STATUS = 'cam4/firebaseAuth/UPDATE_GUEST_STATUS';

const MAX_REQUEST_ACCESS_R_NUMBER = 100000000;

export const NAME = 'firebaseAuth';

/*
  INITIAL STATE
*/
const initialState = Immutable.fromJS({
  authData: null,
  isChatAuthenticated: false,
  authError: '',
  isGuest: null,
  serverOffset: null,
});

/*
  REDUCER
*/
export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SET_AUTH_DATA:
      return state.set('authData', action.authData);

    case SET_IS_CHAT_AUTH:
      if (state.get('authError')) {
        return state.set('isChatAuthenticated', false);
      }

      return state.set('isChatAuthenticated', action.isChatAuthenticated);

    case SET_SERVER_OFFSET:
      return state.set('serverOffset', action.serverOffset);

    case UPDATE_AUTH_ERROR:
      return state.set('authError', action.errorText);

    case UPDATE_GUEST_STATUS:
      return state.set('isGuest', action.isGuest);

    default:
      return state;
  }
}

/*
  INPUT SELECTORS
*/
const authData = (state) => state[NAME].get('authData');
const isChatAuthenticated = (state) => state[NAME].get('isChatAuthenticated');
const authError = (state) => state[NAME].get('authError');
const isGuest = (state) => state[NAME].get('isGuest');
const serverOffset = (state) => state[NAME].get('serverOffset');

/*
  ACTIONS
*/
function setAuthData(data) {
  return {
    type: SET_AUTH_DATA,
    authData: data,
  };
}

function setChatAuthentication(isAuthenticated) {
  return {
    type: SET_IS_CHAT_AUTH,
    isChatAuthenticated: isAuthenticated,
  };
}

function setServerOffset(offset) {
  return {
    type: SET_SERVER_OFFSET,
    serverOffset: offset,
  };
}

function updateAuthError(errorText) {
  return {
    type: UPDATE_AUTH_ERROR,
    errorText,
  };
}

function updateGuestStatus(status) {
  return {
    type: UPDATE_GUEST_STATUS,
    isGuest: status,
  };
}

function initWebPresence(roomName, username) {
  const connectedRef = FirebaseMain.child(firebaseNode.CONNECTED_REF);

  connectedRef.on('value', (snapshot) => {
    const isConnected = !!snapshot.val();
    if (isConnected) {
      const pushNode = FirebaseMain.child('p').child(roomName).child(username)
        .push();
      pushNode.onDisconnect().remove();
      pushNode.set(1);
    }
  });
}

function doFirebaseAuth(cam4FirebaseHost, tokenChat, roomName, username, guestNodeName) {
  initFirebase(cam4FirebaseHost);
  const baseConnectionRef = FirebaseMain.child(firebaseNode.CHAT_ROOMS);
  const serverTimeOffsetRef = FirebaseMain.child(firebaseNode.SERVER_TIME_OFFSET);

  return (dispatch) =>
    baseConnectionRef.authWithCustomToken(tokenChat, (error) => {
      if (error) {
        console.warn('FIREBASE_AUTH_ERROR ', cam4FirebaseHost);
      } else {
        console.info('FIREBASE_AUTH_SUCCESS ', cam4FirebaseHost);
        serverTimeOffsetRef.once('value', (snap) => {
          dispatch(setServerOffset(snap.val()));
        });

        if (!guestNodeName) {
          initWebPresence(roomName, username);
        }
        dispatch(setChatAuthentication(true));
      }

      FirebaseMain.onAuth((authData) => {
        if (authData) {
          console.info('Authenticated to Firebase.', authData.auth);
        } else {
          console.warn('Firebase Client unauthenticated.');
          // TODO: inform webchat of failed connection
        }
      });
    });
}

/*
  API CALLS
*/

// send request to webchat server for auth data needed for firebase initializing
function initRoom(roomName, userName, accessHash, mobilePhoneUsr, pass) {
  let username = userName;
  if (username) {
    username = username.toLowerCase();
  }
  const rnd = Math.floor((Math.random() * MAX_REQUEST_ACCESS_R_NUMBER) + 1);
  const isGuest = (!username || username === '') && (!accessHash || accessHash === '');

  const webChatRequestUrl = isGuest ? GUEST_CHAT_REQUEST_URL : AUTHENTICATED_CHAT_REQUEST_URL;
  const parsedWebChatRequestUrl = ApiService.substitute(
    webChatRequestUrl,
    { rnd, roomName, accessHash, username, pass }
  );

  function roomAccessFailed(error) {
    // TODO: write logic for room access failure and return dispatched action
    console.warn(error);

    return null;
  }

  /*
    HELPERS
  */
  function authDataStatusHandler(status, isGuest) {
    return (dispatch) => {
      switch (status) {
        case 'bannedForRoom':
          dispatch(updateAuthError('status: banned from room'));
          break;

        case 'alreadyInTheRoom':
          dispatch(updateAuthError('status: already in the room'));
          break;

        case 'privateRoom':
          if (isGuest) {
            console.info('status: guest in private room');
            break;
          } else {
            console.info('status: authenticated user in private room');
            break;
          }

        case 'userIsFromBlockedCountryOrRegion':
          dispatch(updateAuthError('status: user is from blocked country or region'));
          break;

        case 'wrongPassword':
          dispatch(updateAuthError('status: wrong password'));
          break;

        case 'success':
          console.info('status: success');
          break;

        default:
          dispatch(updateAuthError('status: failed'));
          roomAccessFailed();
          break;
      }
    };
  }

  function roomAccessSuccess(roomName, username, authData, isGuest) {
    return (dispatch) => {
      const { shard, token, guestNodeName } = authData;
      dispatch(setAuthData(authData));

      if (isGuest) {
        return dispatch(doFirebaseAuth(shard, token, roomName, username, guestNodeName));
      }

      return dispatch(doFirebaseAuth(shard, token, roomName, username));
    };
  }

  return (dispatch) =>
    ApiService.get(parsedWebChatRequestUrl)
      .then((response) => {
        dispatch(updateGuestStatus(isGuest));
        dispatch(authDataStatusHandler(response.data.status, isGuest));

        return dispatch(roomAccessSuccess(roomName, username, response.data, isGuest));
      })
      .catch((error) => {
        roomAccessFailed(error);
        dispatch(updateAuthError(error));
        dispatch(setChatAuthentication(false));
      });
}

function doFirebaseAuthForInbox(cam4FirebaseHostForInbox, cam4FirebaseTokenForInbox) {
  initFirebase(cam4FirebaseHostForInbox, 'Notifications');

  return (dispatch) =>
    FirebaseMain.authWithCustomToken(cam4FirebaseTokenForInbox, (error) => {
      if (error) {
        console.warn('FIREBASE_AUTH_ERROR ', cam4FirebaseHostForInbox);
      } else {
        dispatch(setChatAuthentication(true));
      }

      FirebaseMain.onAuth((authData) => {
        if (!authData) {
          console.warn('Firebase Client unauthenticated.');
          // TODO
          // inform webchat of failed connection
        }
      });
    });
}

/*
  EXPORTS
*/
export const selector = createStructuredSelector({
  firebaseAuthData: createStructuredSelector({
    authData,
    authError,
    isChatAuthenticated,
    isGuest,
    serverOffset,
  }),
});

export const actionCreators = {
  initRoom,
  initWebPresence,
  doFirebaseAuth,
  doFirebaseAuthForInbox,
};
